/**
 * Price-histogram math for the WHAT popover's price slider (#870 slice 5,
 * toolkit: `hrtk-sbp-search-bar__price-histogram`).
 *
 * The toolkit computes its buckets server-side over the whole catalog
 * (`Shortcode::get_price_data`). The widget has no such endpoint, so the
 * distribution is derived client-side from the properties already loaded for
 * the current search (Eli's #870 decision 1). Two consequences are deliberate:
 * the sample is only as wide as the pages the visitor has loaded, and the
 * caller must feed prices from a query with the price filter STRIPPED —
 * otherwise narrowing the range starves the histogram that draws it.
 *
 * Pure functions, no React: the bucketing and the dim rule are the parts worth
 * pinning in tests.
 */

/** Bars across the track. Dense enough to read as a distribution at popover width. */
export const PRICE_BUCKET_COUNT = 28

/**
 * Count prices into `bucketCount` equal-width buckets spanning [min, max].
 * Values outside the bounds clamp into the end buckets (a stale price column
 * can sit outside the meta range); `max` itself lands in the LAST bucket, not
 * a phantom one past the end. Returns [] when the range is degenerate.
 */
export function buildPriceBuckets(
  prices: number[],
  min: number,
  max: number,
  bucketCount: number = PRICE_BUCKET_COUNT,
): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min || bucketCount < 1) return []
  const buckets = new Array<number>(bucketCount).fill(0)
  const width = (max - min) / bucketCount
  let counted = 0
  for (const price of prices) {
    if (typeof price !== 'number' || !Number.isFinite(price)) continue
    const raw = Math.floor((price - min) / width)
    const idx = raw < 0 ? 0 : raw >= bucketCount ? bucketCount - 1 : raw
    buckets[idx]++
    counted++
  }
  return counted === 0 ? [] : buckets
}

/**
 * Bar height as a percentage of the histogram box (toolkit parity,
 * search-bar.php: `max(4, round(count / bucketMax * 100))`, and a 2% stub for
 * an empty bucket so the baseline stays legible).
 */
export function bucketHeightPct(count: number, bucketMax: number): number {
  if (count <= 0 || bucketMax <= 0) return 2
  return Math.max(4, Math.round((count / bucketMax) * 100))
}

/**
 * Whether bucket `index` falls entirely outside the selected range and should
 * render dim.
 *
 * The edge bucket is DELIBERATELY left lit when it merely touches the
 * selection — a bucket spanning 464.28-500 stays active at a minimum of 500
 * even though almost none of its properties qualify. That is the toolkit's own
 * rule ported verbatim (any overlap keeps a bar active), and a sharper one is
 * not computable here: this function receives bucket COUNTS and range bounds,
 * never the individual prices, so it cannot know how many properties in the
 * edge bucket actually sit inside the selection. Nothing downstream reads it —
 * no query, no result count, no pill — it only decides which bars dim. Please
 * do not "fix" the overlap rule without also changing what the histogram is
 * fed.
 */
export function isBucketDimmed(
  index: number,
  bucketCount: number,
  min: number,
  max: number,
  selectedMin: number,
  selectedMax: number,
): boolean {
  if (bucketCount < 1 || max <= min) return false
  const width = (max - min) / bucketCount
  const lo = min + index * width
  const hi = lo + width
  return hi < selectedMin || lo > selectedMax
}

/** Snap to the nearest step and clamp into [lo, hi]. */
export function snapClamp(value: number, step: number, lo: number, hi: number): number {
  const safeStep = step > 0 ? step : 1
  const snapped = Math.round(value / safeStep) * safeStep
  return snapped < lo ? lo : snapped > hi ? hi : snapped
}
